import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, from, Observable, switchMap, tap, throwError } from 'rxjs';

import { AuthService } from '../../../auth/services/auth.service';
import {
  ACCESS_TOKEN_KEY,
  LEGACY_API_KEY,
  REFRESH_TOKEN_KEY,
} from '../../../auth/services/auth.utils';
import { StorageContextService } from '../../../context/storage-context/storage-context.service';
import { AppConfiguration } from '../../services/configuration/app.configuration';
import {
  isConfiguredStretchitBusinessApiRequest,
  isUserProfileSnapshotRequest,
} from '../stretchit-api-headers/request-classifier';
import { AuthRefreshCoordinator } from './auth-refresh-coordinator.service';
import { AuthRefreshDiagnosticsService } from './auth-refresh-diagnostics.service';
import {
  AuthRefreshReason,
  JwtTokenState,
  classifyAuthSession,
  getHttpStatus,
  isAccountLockedResponse,
  requireSuccessfulAuthRefresh,
} from './auth-refresh.models';
import { JwtTokenInspector } from './jwt-token-inspector';

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(AppConfiguration);
  const coordinator = inject(AuthRefreshCoordinator);
  const diagnostics = inject(AuthRefreshDiagnosticsService);
  const storage = inject(StorageContextService);
  const tokenInspector = inject(JwtTokenInspector);
  const injector = inject(Injector);

  if (!isConfiguredStretchitBusinessApiRequest(req, config)) return next(req);

  const session = readStoredSession(storage);
  const sessionMode = classifyAuthSession(session);
  if (sessionMode === 'public') return next(req);

  if (sessionMode === 'legacy_only') {
    return sendWithResponseRecovery({
      req,
      next,
      coordinator,
      diagnostics,
      storage,
      tokenInspector,
      injector,
      sentSessionMode: sessionMode,
      tokenExchangeCheckpoint: coordinator.captureTokenExchangeCheckpoint(),
    });
  }

  const tokenState = tokenInspector.inspect(session.accessToken);
  const mustRefreshBeforeSend =
    !isUserProfileSnapshotRequest(req, config) &&
    (tokenState.kind === 'absent' ||
      (tokenState.kind === 'near_expiry' && !!session.refreshToken) ||
      tokenState.kind === 'expired' ||
      tokenState.kind === 'malformed');

  if (!mustRefreshBeforeSend) {
    return sendWithResponseRecovery({
      req,
      next,
      coordinator,
      diagnostics,
      storage,
      tokenInspector,
      injector,
      sentSessionMode: sessionMode,
      tokenExchangeCheckpoint: null,
    });
  }

  return from(coordinator.refresh('expiring_request')).pipe(
    tap(requireSuccessfulAuthRefresh),
    switchMap(() =>
      sendWithResponseRecovery({
        req,
        next,
        coordinator,
        diagnostics,
        storage,
        tokenInspector,
        injector,
        sentSessionMode: sessionMode,
        tokenExchangeCheckpoint: null,
      })
    )
  );
};

function sendWithResponseRecovery(input: {
  req: HttpRequest<unknown>;
  next: HttpHandlerFn;
  coordinator: AuthRefreshCoordinator;
  diagnostics: AuthRefreshDiagnosticsService;
  storage: StorageContextService;
  tokenInspector: JwtTokenInspector;
  injector: Injector;
  sentSessionMode: 'legacy_only' | 'jwt_capable';
  tokenExchangeCheckpoint: number | null;
}): Observable<HttpEvent<unknown>> {
  const {
    req,
    next,
    coordinator,
    diagnostics,
    storage,
    tokenInspector,
    injector,
    sentSessionMode,
    tokenExchangeCheckpoint,
  } = input;
  const sentTokenState = tokenInspector.inspect(storage.getItem(ACCESS_TOKEN_KEY));
  const send = (): Observable<HttpEvent<unknown>> =>
    next(req).pipe(
      tap({
        error: (error: unknown) => {
          if (
            isAccountLockedResponse(error) &&
            classifyAuthSession(readStoredSession(storage)) !== 'public'
          ) {
            // Resolved lazily: AuthService depends on HttpClient, which depends on this interceptor
            injector.get(AuthService).handleAccountLocked(error);
          }
        },
      })
    );

  return send().pipe(
    catchError((error: unknown): Observable<HttpEvent<unknown>> => {
      const status = getHttpStatus(error);
      // The lock is final: a refresh would not lift it, and a replay would only loop
      if ((status !== 401 && status !== 403) || isAccountLockedResponse(error)) {
        return throwError(() => error);
      }

      const currentSessionMode = classifyAuthSession(readStoredSession(storage));
      if (
        currentSessionMode === 'public' ||
        (sentSessionMode === 'jwt_capable' && currentSessionMode !== 'jwt_capable')
      ) {
        return throwError(() => error);
      }

      const reason = getResponseRefreshReason(status);
      const reportLegacy403 = status === 403;
      const recovery =
        sentSessionMode === 'legacy_only' && tokenExchangeCheckpoint !== null
          ? coordinator.exchangeLegacyApiKeyAfterCheckpoint(tokenExchangeCheckpoint)
          : coordinator.refresh(reason);

      return from(recovery).pipe(
        switchMap(result => {
          if (result.kind !== 'success') {
            if (reportLegacy403) {
              diagnostics.reportLegacy403({
                method: req.method,
                url: req.urlWithParams,
                jwtState: toDiagnosticJwtState(sentTokenState),
                refreshResultKind: result.kind,
                replayOutcome: 'not_attempted',
              });
            }

            requireSuccessfulAuthRefresh(result);
          }

          const replay = send();
          if (!reportLegacy403) return replay;

          return replay.pipe(
            tap(event => {
              if (event instanceof HttpResponse) {
                diagnostics.reportLegacy403({
                  method: req.method,
                  url: req.urlWithParams,
                  jwtState: toDiagnosticJwtState(sentTokenState),
                  refreshResultKind: result.kind,
                  replayOutcome: 'success',
                });
              }
            }),
            catchError((replayError: unknown) => {
              diagnostics.reportLegacy403({
                method: req.method,
                url: req.urlWithParams,
                jwtState: toDiagnosticJwtState(sentTokenState),
                refreshResultKind: result.kind,
                replayOutcome: { status: getHttpStatus(replayError) ?? 0 },
              });

              return throwError(() => replayError);
            })
          );
        })
      );
    })
  );
}

function readStoredSession(storage: StorageContextService): {
  accessToken: string;
  refreshToken: string;
  legacyApiKey: string;
} {
  return {
    accessToken: storage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: storage.getItem(REFRESH_TOKEN_KEY),
    legacyApiKey: storage.getItem(LEGACY_API_KEY),
  };
}

function getResponseRefreshReason(status: 401 | 403): AuthRefreshReason {
  if (status === 401) return 'response_401';

  return 'response_403';
}

function toDiagnosticJwtState(
  state: JwtTokenState
): 'valid' | 'near_expiry' | 'expired' | 'malformed' {
  if (state.kind === 'valid' || state.kind === 'near_expiry' || state.kind === 'expired') {
    return state.kind;
  }

  return 'malformed';
}
